/* Persistent token storage — durable on iOS + Android, works on web.
 *
 * Problem this solves (Build 3/4 iOS TestFlight bug):
 *   The old code stored the JWT only in browser localStorage. On iOS
 *   Capacitor's WKWebView, localStorage IS persisted to disk, BUT it can be
 *   silently evicted (WebKit sometimes trims per-origin storage during
 *   memory pressure or on iOS updates). When that happens, the user is
 *   silently logged out on next app launch. On web this is fine because a
 *   cookie or a fresh login covers it — on Capacitor native it means the
 *   user is stuck at the login screen.
 *
 * Solution: dual-write to (a) localStorage for synchronous reads inside
 * axios interceptors and (b) @capacitor/preferences which is backed by
 * NSUserDefaults on iOS and SharedPreferences on Android — genuinely
 * durable native storage. On boot we read Preferences and hydrate
 * localStorage back if it was evicted.
 *
 * Web: @capacitor/preferences falls back to localStorage internally, so
 * this file adds zero overhead on the browser.
 */
import { Preferences } from "@capacitor/preferences";

const KEY = "sr_token";

/** Read from localStorage (synchronous). */
export function readTokenSync() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

/** Write to BOTH localStorage AND Preferences. Fire-and-forget for
 *  Preferences — localStorage is what the axios interceptor uses. */
export function writeToken(token) {
  try { localStorage.setItem(KEY, token); } catch { /* ignore */ }
  Preferences.set({ key: KEY, value: token }).catch(() => { /* non-fatal */ });
}

/** Remove from BOTH. Await the Preferences call so subsequent hydrations
 *  can't resurrect an old token. */
export async function clearToken() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  try { await Preferences.remove({ key: KEY }); } catch { /* ignore */ }
}

/** On boot, restore the token from durable native storage back into
 *  localStorage if localStorage was evicted (iOS memory pressure, etc.).
 *  Runs once at AuthProvider mount. */
export async function hydrateTokenFromNative() {
  const inLocal = readTokenSync();
  if (inLocal) return inLocal;
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (value) {
      try { localStorage.setItem(KEY, value); } catch { /* ignore */ }
      return value;
    }
  } catch { /* ignore */ }
  return null;
}
